import { type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "./auth-context";
import { FullScreenLoader } from "./full-screen-loader";
import { type Role } from "./types";
import { AppRoutes } from "../components/app/routes";

const render = (children: ReactNode) => (children ? <>{children}</> : <Outlet />);

/**
 * Gate that requires an authenticated session. Unauthenticated users are sent
 * to the login screen, preserving where they were headed so login can bounce
 * them back. Usable as a layout route (renders `<Outlet/>`) or a wrapper.
 */
export const RequireAuth = ({ children }: { children?: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullScreenLoader />;
  if (status === "unauthenticated") {
    return <Navigate to={AppRoutes.LOGIN} replace state={{ from: location }} />;
  }
  return render(children);
};

/**
 * Gate that requires a specific role (or any of several). Assumes it sits under
 * `RequireAuth`. A user with no role yet (autoregistro sin rol) goes to the
 * pending screen; a role mismatch is redirected to the app root, which
 * re-routes by role. Passing an array admits any of the listed roles (e.g. a
 * screen shared by supervisor + contact_center).
 */
export const RequireRole = ({
  role,
  children,
}: {
  role: NonNullable<Role> | NonNullable<Role>[];
  children?: ReactNode;
}) => {
  const { status, user } = useAuth();

  if (status === "loading") return <FullScreenLoader />;
  if (status === "unauthenticated") {
    return <Navigate to={AppRoutes.LOGIN} replace />;
  }
  if (user?.role == null) {
    return <Navigate to={AppRoutes.PENDING} replace />;
  }
  const allowed = Array.isArray(role) ? role : [role];
  if (!allowed.includes(user.role)) {
    return <Navigate to={AppRoutes.ROOT} replace />;
  }
  return render(children);
};
